import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { SocketService } from './socket.service'
import { IdentityService } from 'main/services/identity.service'
import { ActiveToast, ToastrService } from 'ngx-toastr'

export interface NotificationOptions {
  sticky: boolean,
  url: string,
  markup: boolean,
  click: CallableFunction,
}

@Injectable({ providedIn: 'root' })
export class NotifyService {
  constructor (
    private translate: TranslateService,
    private socket: SocketService,
    private identity: IdentityService,
    private toastr: ToastrService,
  ) {
    socket.subject('ui:notification').subscribe(data => {
      if ((data.title || data.text) &&
          (!data.recipient || data.recipient === '@all' || data.recipient === identity.user?.username)
      ) {
        if (data.subject !== 'ltfs-job' || identity.hasPermission('ltfs:manage')) {
          this.custom('info', data.title, data.markup ?? data.text, {
            url: data.url,
            sticky: data.sticky,
            markup: !!data.markup,
          })
        }
      }
    })
  }

  info (title: string, text = ''): void {
    this.custom('info', title, text)
  }

  success (title: string, text = ''): void {
    this.custom('success', title, text)
  }

  warning (title: string, text = ''): void {
    this.custom('warning', title, text)
  }

  error (title: string, text = ''): void {
    this.custom('error', title, text)
  }

  process (title: string, text = ''): ActiveToast<any> {
    return this.toastr.show(text, title, {
      toastClass: 'ngx-toastr toast-process',
      disableTimeOut: true,
      tapToDismiss: false,
    })
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async errorFromResponse (title: string, response: any): Promise<void> {
    title = title || this.translate.instant('Error')
    const contentType = response.headers?.get?.('content-type')
    if (response.json && contentType === 'application/json') {
      try {
        response = { data: await response.json(), status: response.status }
      } catch { }
    }
    if (response.text && !contentType?.startsWith('text/html')) {
      try {
        response = { data: await response.text(), status: response.status }
      } catch { }
    }
    if (response.status === 400 && response?.data instanceof Array) {
      // DRF many-serializer validation error
      for (const item of response.data) {
        if (Object.keys(item).length) {
          await this.errorFromResponse(title, {
            ...response,
            data: item,
          })
        }
      }
      return
    }
    let text = response.toString()
    if (response.data?.error) {
      text = response.data.error
    } else if (response.data?.error_message) {
      text = response.data.error_message
    } else if (response.data?.message) {
      text = response.data.message
    } else if (response.data?.detail) {
      text = response.data.detail
    } else if (response.status === 400 && Object.keys(response.data).every(k => response.data[k] instanceof Array)) {
      // DRF single serializer validation error
      const messages = Object.keys(response.data).map(k => `${k}: ${response.data[k].join(', ')}`)
      text = messages.join(' \n')
    } else if (response.data) {
      text = response.data
    }

    if (text === '[object Object]' || text === '[object Response]') {
      text = ''
    }

    if (text.length === 1) {
      this.error(title, text[0])
    } else {
      this.error(title, text)
    }
  }

  finishProcess (toast: ActiveToast<any>, type: 'success'|'info'|'warning'|'error', title?: string, message?: string): void {
    if (title) {
      toast.toastRef.componentInstance.title = title
    }
    if (message) {
      toast.toastRef.componentInstance.message = message
    }
    toast.toastRef.componentInstance.toastClasses = `ngx-toastr toast-${type}`
    toast.toastRef.componentInstance.tapToDismiss = true
    setTimeout(() => {
      this.toastr.clear(toast.toastId)
    }, 5000)
  }

  custom (type: string, title: string, text = '', options: Partial<NotificationOptions> = {}): ActiveToast<any> {
    const additionalClasses = options.markup ? 'markup' : ''
    const toast = this.toastr.show(text, title, {
      toastClass: `ngx-toastr toast-${type} ${additionalClasses}`,
      disableTimeOut: !!options.sticky,
      closeButton: !!options.sticky,
      tapToDismiss: !options.url,
      enableHtml: !!options.markup,
    })

    if (options.url && !options.markup) {
      toast.onTap.subscribe(() => {
        this.toastr.clear(toast.toastId)
        if (/^[A-Za-z][\d+.A-Z\\a-z-]*:/.test(options.url!)) {
          window.open(options.url, '_blank')
        } else {
          location.href = options.url!
        }
      })
    }

    if (options.click) {
      toast.onTap.subscribe(() => {
        this.toastr.clear(toast.toastId)
        options.click!()
      })
    }

    return toast
  }
}
